import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Tag, Typography, message, Modal, Input, Select, Dropdown, Checkbox, Collapse, Drawer, List, Popconfirm } from 'antd';
import { 
    PlusOutlined, 
    SearchOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    SettingOutlined, 
    FolderOutlined, 
    FolderOpenOutlined,
    HistoryOutlined, 
    ShareAltOutlined,
    FileOutlined,
    FileTextOutlined,
    CheckSquareOutlined,
    DownOutlined,
    RightOutlined,
    DatabaseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TestItemForm } from './TestItemForm';
import { testItemService } from '../../services/testItemService';
import { userService } from '../../services/userService';
import { sharedStepService } from '../../services/sharedStepService';
import { TestItem, Folder, TestItemType, TestItemPriority, TestItemSeverity, TestItemStatus, TestItemStep, TestItemCheckItem, TestItemCreate, TestData } from '../../types/testItem';
import { SharedStep, SharedStepCreate, Step } from '../../types/sharedStep';
import { SharedStepsList } from '../shared/SharedStepsList';
import type { ColumnType } from 'antd/es/table';
import type { ExpandableConfig } from 'antd/lib/table/interface';
import { AddSharedStepsModal } from '../shared/AddSharedStepsModal';
import { SelectTestCaseModal } from '../shared/SelectTestCaseModal';
import { SelectTestDataModal } from './SelectTestDataModal';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface TestItemListProps {
    projectId: number;
    items: TestItem[];
    folders: Folder[];
    loading: boolean;
    onEdit: (item: TestItem) => void;
    onDelete: (item: TestItem) => Promise<void>;
    onEditFolder: (folder: Folder) => void;
    onDeleteFolder: (folder: Folder) => Promise<void>;
    onDataChange: () => void;
}

interface TableItem extends Omit<TestItem, 'version' | 'created_at' | 'updated_at'> {
    key: string;
    isFolder: boolean;
    level: number;
    folder_id?: number;
    components?: string[];
    description?: string;
    estimate_minutes?: number;
    labels?: string[];
    version?: number;
    created_by?: number;
    created_at?: string;
    updated_by?: number;
    updated_at?: string;
    steps?: TestItemStep[];
    check_items?: TestItemCheckItem[];
    display_number?: string;
    sequential_number?: number;
}

interface ColumnVisibility {
    [key: string]: boolean;
}

const TestItemList: React.FC<TestItemListProps> = ({ 
    projectId, 
    items,
    folders,
    loading, 
    onEdit, 
    onDelete,
    onEditFolder,
    onDeleteFolder,
    onDataChange
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [selectedType, setSelectedType] = useState<TestItemType | ''>('');
    const [selectedPriority, setSelectedPriority] = useState<TestItemPriority | ''>('');
    const [selectedSeverity, setSelectedSeverity] = useState<TestItemSeverity | ''>('');
    const [selectedStatus, setSelectedStatus] = useState<TestItemStatus | ''>('');
    const [selectedItem, setSelectedItem] = useState<TestItem | null>(null);
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
        const savedVisibility = localStorage.getItem(`columnVisibility_${projectId}`);
        return savedVisibility ? JSON.parse(savedVisibility) : {
            display_number: true,
            title: true,
            type: true,
            priority: true,
            severity: true,
            status: true,
            steps: true,
            check_items: true,
            description: false,
            components: false,
            folder: false,
            estimate: false,
            labels: false,
            version: false,
            createdBy: false,
            createdAt: false,
            updatedBy: false,
            updatedAt: false,
            actions: true
        };
    });
    const [userNames, setUserNames] = useState<Record<number, string>>({});
    const [sharedStepsDrawerVisible, setSharedStepsDrawerVisible] = useState(false);
    const [sharedSteps, setSharedSteps] = useState<SharedStep[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>();
    const [addStepsModalVisible, setAddStepsModalVisible] = useState(false);
    const [selectedFolderForSteps, setSelectedFolderForSteps] = useState<number | null>(null);
    const [selectTestCaseModalVisible, setSelectTestCaseModalVisible] = useState(false);
    const [selectedSharedStep, setSelectedSharedStep] = useState<SharedStep | null>(null);
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [showSharedSteps, setShowSharedSteps] = useState<Record<string, boolean>>({});
    const [expandedFolders, setExpandedFolders] = useState<number[]>([]);
    const [folderTestData, setFolderTestData] = useState<Record<number, TestData[]>>({});
    const [isTestDataModalVisible, setIsTestDataModalVisible] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

    useEffect(() => {
        localStorage.setItem(`columnVisibility_${projectId}`, JSON.stringify(columnVisibility));
    }, [columnVisibility, projectId]);

    const fetchUserName = async (userId: number) => {
        if (!userId || userNames[userId]) return;
        try {
            const user = await userService.getUserById(userId);
            setUserNames(prev => ({
                ...prev,
                [userId]: `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User ${userId}`
            }));
        } catch (error) {
            console.error('Failed to fetch user name:', error);
            // Set a fallback name if the user fetch fails
            setUserNames(prev => ({
                ...prev,
                [userId]: `User ${userId}`
            }));
        }
    };

    useEffect(() => {
        // Fetch user names for all unique user IDs in the items
        const uniqueUserIds = new Set<number>();
        items.forEach(item => {
            if (item.created_by) uniqueUserIds.add(item.created_by);
            if (item.updated_by) uniqueUserIds.add(item.updated_by);
        });
        uniqueUserIds.forEach(userId => fetchUserName(userId));
    }, [items]);

    const getFolderPath = (folderId: number | null): string => {
        if (!folderId) return '';
        
        const getPath = (id: number, path: string[] = []): string[] => {
            const folder = folders.find(f => f.id === id);
            if (!folder) return path;
            
            path.unshift(folder.name);
            if (folder.parent_id) {
                return getPath(folder.parent_id, path);
            }
            return path;
        };

        return getPath(folderId).join(' / ');
    };

    const transformTestItems = (items: TestItem[], level: number = 0): TableItem[] => {
        return items.map(item => ({
            ...item,
            key: item.id.toString(),
            isFolder: false,
            level,
            folder_id: item.folder_id,
            project_id: item.project_id,
            title: item.name || item.title,
            component: item.component,
            description: item.description,
            estimate_minutes: item.estimate_minutes,
            labels: item.labels,
            created_by: item.created_by,
            created_at: item.created_at,
            updated_by: item.updated_by,
            updated_at: item.updated_at,
            steps: item.steps || [],
            check_items: item.check_items || [],
            display_number: item.display_number,
            sequential_number: item.sequential_number
        }));
    };

    const handleExpand = (record: TableItem) => {
        const newExpandedRows = { ...expandedRows };
        newExpandedRows[record.key] = !newExpandedRows[record.key];
        setExpandedRows(newExpandedRows);

        // Если это папка и она раскрывается, загружаем её тестовые данные
        if (record.isFolder && newExpandedRows[record.key]) {
            fetchFolderTestData(record.id);
        }
    };

    const handleShowSharedSteps = (record: TableItem) => {
        setShowSharedSteps(prev => ({
            ...prev,
            [record.key]: !prev[record.key]
        }));
    };

    const renderExpandedContent = (record: TableItem) => {
        if (record.isFolder) {
            const folderSteps = sharedSteps.filter(step => step.folder_id === record.id);
            const folderData = folderTestData[record.id] || [];

            return (
                <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* Секция тестовых данных */}
                        <div style={{ 
                            border: '1px solid #f0f0f0', 
                            borderRadius: '4px', 
                            padding: '16px',
                            backgroundColor: 'white'
                        }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <Space>
                                    <DatabaseOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                                    <Typography.Title level={5} style={{ margin: 0 }}>
                                        Тестовые данные
                                    </Typography.Title>
                                </Space>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleAddTestData(record as unknown as Folder)}
                                >
                                    Добавить данные
                                </Button>
                            </Space>
                            {folderData.length > 0 ? (
                                <List
                                    dataSource={folderData}
                                    renderItem={data => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <Space>
                                                        <DatabaseOutlined style={{ color: '#1890ff' }} />
                                                        <Typography.Text strong>{data.name}</Typography.Text>
                                                    </Space>
                                                }
                                                description={data.description}
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Typography.Text type="secondary">
                                    Нет добавленных тестовых данных
                                </Typography.Text>
                            )}
                        </div>

                        {/* Секция общих шагов */}
                        <div style={{ 
                            border: '1px solid #f0f0f0', 
                            borderRadius: '4px', 
                            padding: '16px',
                            backgroundColor: 'white',
                            marginTop: '16px'
                        }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <Space>
                                    <ShareAltOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                                    <Typography.Title level={5} style={{ margin: 0 }}>
                                        Общие шаги
                                    </Typography.Title>
                                </Space>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFolderForSteps(record.id);
                                        setAddStepsModalVisible(true);
                                    }}
                                >
                                    Добавить шаги
                                </Button>
                            </Space>
                            {folderSteps.length > 0 ? (
                                <List
                                    dataSource={folderSteps}
                                    renderItem={step => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <Space>
                                                        <FileTextOutlined style={{ color: '#1890ff' }} />
                                                        <Typography.Text strong>{step.name}</Typography.Text>
                                                    </Space>
                                                }
                                                description={
                                                    <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
                                                        {step.description && (
                                                            <Typography.Text type="secondary">
                                                                {step.description}
                                                            </Typography.Text>
                                                        )}
                                                        <List
                                                            size="small"
                                                            dataSource={step.steps}
                                                            renderItem={(s, index) => (
                                                                <List.Item style={{ padding: '8px 0' }}>
                                                                    <Space direction="vertical" style={{ width: '100%' }}>
                                                                        <Typography.Text strong>
                                                                            Шаг {index + 1}: {s.action}
                                                                        </Typography.Text>
                                                                        <Typography.Text>
                                                                            Ожидаемый результат: {s.result?.expected}
                                                                        </Typography.Text>
                                                                    </Space>
                                                                </List.Item>
                                                            )}
                                                        />
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Typography.Text type="secondary">
                                    Нет добавленных общих шагов
                                </Typography.Text>
                            )}
                        </div>
                    </Space>
                </div>
            );
        } else if (record.type === TestItemType.TEST_CASE) {
            return (
                <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                    <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                        <FileTextOutlined style={{ color: '#1890ff' }} /> Шаги тест-кейса
                    </Typography.Title>
                    <List
                        dataSource={record.steps}
                        renderItem={(step, index) => (
                            <List.Item style={{ padding: '8px 0' }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Typography.Text strong>
                                        Шаг {index + 1}: {step.action}
                                    </Typography.Text>
                                    <Typography.Text>
                                        Ожидаемый результат: {step.expected_result}
                                    </Typography.Text>
                                    {step.test_data && (
                                        <Typography.Text>
                                            Тестовые данные: {step.test_data}
                                        </Typography.Text>
                                    )}
                                </Space>
                            </List.Item>
                        )}
                    />
                </div>
            );
        } else if (record.type === TestItemType.CHECKLIST) {
            return (
                <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                    <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                        <CheckSquareOutlined style={{ color: '#52c41a' }} /> Проверки чек-листа
                    </Typography.Title>
                    <List
                        dataSource={record.check_items}
                        renderItem={(item, index) => (
                            <List.Item style={{ padding: '8px 0' }}>
                                <Space>
                                    <Checkbox checked={item.is_checked} disabled />
                                    <Typography.Text>
                                        {index + 1}. {item.description}
                                    </Typography.Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                </div>
            );
        }
        return null;
    };

    const getTableData = (): TableItem[] => {
        const tableData: TableItem[] = [];

        // Add root level test items (those without a folder)
        const rootTestItems = items.filter(item => !item.folder_id);
        tableData.push(...transformTestItems(rootTestItems, 0));

        // Add folders and their contents
        const addFolderAndItems = (folder: Folder, level: number) => {
            // Add the folder itself
            tableData.push({
                key: `folder-${folder.id}`,
                id: folder.id,
                title: folder.name,
                type: TestItemType.TEST_CASE,
                priority: TestItemPriority.LOW,
                severity: TestItemSeverity.TRIVIAL,
                status: TestItemStatus.DRAFT,
                folder_id: folder.parent_id,
                isFolder: true,
                level: level,
                project_id: projectId,
            } as TableItem);

            // If folder is expanded, add its contents
            if (expandedRows[`folder-${folder.id}`]) {
            // Add test items in this folder
            const folderTestItems = items.filter(item => item.folder_id === folder.id);
            tableData.push(...transformTestItems(folderTestItems, level + 1));

            // Recursively add subfolders
            const subfolders = folders.filter(f => f.parent_id === folder.id);
            subfolders.forEach(subfolder => addFolderAndItems(subfolder, level + 1));
            }
        };

        // Start with root folders (those without a parent)
        const rootFolders = folders.filter(folder => !folder.parent_id);
        rootFolders.forEach(folder => addFolderAndItems(folder, 0));

        return tableData;
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
    };

    const handleTypeChange = (value: TestItemType | '') => {
        setSelectedType(value);
    };

    const handlePriorityChange = (value: TestItemPriority | '') => {
        setSelectedPriority(value);
    };

    const handleSeverityChange = (value: TestItemSeverity | '') => {
        setSelectedSeverity(value);
    };

    const handleStatusChange = (value: TestItemStatus | '') => {
        setSelectedStatus(value);
    };

    const handleEditItem = (item: TestItem) => {
        onEdit(item);
    };

    const handleDelete = async (item: TableItem) => {
        try {
            if (item.isFolder) {
                await onDeleteFolder(item as unknown as Folder);
            } else {
                await onDelete(item as unknown as TestItem);
            }
        } catch (error) {
            message.error(item.isFolder 
                ? t('testItems.errors.deleteFolderFailed')
                : t('testItems.errors.deleteFailed')
            );
        }
    };

    const handleColumnVisibilityChange = (column: string, visible: boolean) => {
        setColumnVisibility(prev => ({
            ...prev,
            [column]: visible
        }));
    };

    const columnVisibilityMenu = (
        <div style={{ 
            padding: '8px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '4px'
        }}>
            {Object.entries(columnVisibility).map(([key, visible]) => {
                if (['title', 'type', 'priority', 'severity', 'status', 'actions'].includes(key)) {
                    return null;
                }
                return (
                    <div key={key} style={{ marginBottom: '8px' }}>
                        <Checkbox
                            checked={visible}
                            onChange={(e) => handleColumnVisibilityChange(key, e.target.checked)}
                        >
                            {t(`testItems.columns.${key}`)}
                        </Checkbox>
                    </div>
                );
            })}
        </div>
    );

    const columns: ColumnType<TableItem>[] = [
        {
            title: '#',
            dataIndex: 'display_number',
            key: 'display_number',
            width: 100,
            render: (text: string, record: TableItem) => {
                if (record.isFolder) return null;
                return record.display_number;
            }
        },
        {
            title: t('testItems.columns.title'),
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: TableItem) => {
                const indent = record.level * 20;
                const hasSharedSteps = record.isFolder && sharedSteps.some(step => step.folder_id === record.id);
                const hasTestData = record.isFolder && folderTestData[record.id]?.length > 0;
                const sharedStepsCount = record.isFolder ? sharedSteps.filter(step => step.folder_id === record.id).length : 0;
                
                return (
                    <div style={{ marginLeft: indent }}>
                        {record.isFolder ? (
                            <Space>
                                <Button
                                    type="text"
                                    icon={expandedRows[record.key] ? <FolderOpenOutlined /> : <FolderOutlined />}
                                    onClick={() => handleExpand(record)}
                                />
                                <Typography.Text strong>
                                    {text}
                                </Typography.Text>
                                {hasTestData && (
                                    <Tag icon={<DatabaseOutlined />} color="blue">
                                        {folderTestData[record.id]?.length} тест. данных
                                    </Tag>
                                )}
                                {hasSharedSteps && (
                                    <Tag icon={<ShareAltOutlined />} color="purple">
                                        {sharedStepsCount} предшагов
                                    </Tag>
                                )}
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFolderForSteps(record.id);
                                        setAddStepsModalVisible(true);
                                    }}
                                    title="Добавить предшаги"
                                />
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddTestData(record as unknown as Folder);
                                    }}
                                    title="Добавить тестовые данные"
                                />
                            </Space>
                        ) : (
                            <Space>
                                {record.type === TestItemType.TEST_CASE && (
                                    <>
                                        <Button
                                            type="text"
                                            icon={expandedRows[record.key] ? <DownOutlined /> : <RightOutlined />}
                                            onClick={() => handleExpand(record)}
                                        />
                                        <FileTextOutlined style={{ color: '#1890ff' }} />
                                    </>
                                )}
                                {record.type === TestItemType.CHECKLIST && (
                                    <>
                                        <Button
                                            type="text"
                                            icon={expandedRows[record.key] ? <DownOutlined /> : <RightOutlined />}
                                            onClick={() => handleExpand(record)}
                                        />
                                        <CheckSquareOutlined style={{ color: '#52c41a' }} />
                                    </>
                                )}
                                <Typography.Text>
                                    {text}
                                </Typography.Text>
                            </Space>
                        )}
                    </div>
                );
            }
        },
        {
            title: t('testItems.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (type: TestItemType, record: TableItem) => {
                if (record.isFolder) return null;
                return (
                    <Tag color={type === TestItemType.TEST_CASE ? 'blue' : 'green'}>
                        {t(`testItems.types.${type?.toLowerCase() || ''}`)}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.priority'),
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority: TestItemPriority, record: TableItem) => {
                if (record.isFolder) return null;
                return (
                    <Tag color={
                        priority === TestItemPriority.CRITICAL ? 'red' :
                        priority === TestItemPriority.HIGH ? 'orange' :
                        priority === TestItemPriority.MEDIUM ? 'yellow' :
                        'green'
                    }>
                        {t(`testItems.priorities.${priority?.toLowerCase() || ''}`)}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.severity'),
            dataIndex: 'severity',
            key: 'severity',
            width: 100,
            render: (severity: TestItemSeverity, record: TableItem) => {
                if (record.isFolder) return null;
                return (
                    <Tag color={
                        severity === TestItemSeverity.BLOCKER ? 'red' :
                        severity === TestItemSeverity.CRITICAL ? 'orange' :
                        severity === TestItemSeverity.MAJOR ? 'yellow' :
                        severity === TestItemSeverity.MINOR ? 'blue' :
                        'green'
                    }>
                        {t(`testItems.severities.${severity?.toLowerCase() || ''}`)}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: TestItemStatus, record: TableItem) => {
                if (record.isFolder) return null;
                return (
                    <Tag color={
                        status === TestItemStatus.COMPLETED ? 'green' :
                        status === TestItemStatus.IN_PROGRESS ? 'blue' :
                        status === TestItemStatus.REVIEWED ? 'purple' :
                        status === TestItemStatus.OBSOLETE ? 'red' :
                        status === TestItemStatus.DEPRECATED ? 'gray' :
                        'default'
                    }>
                        {t(`testItems.statuses.${status?.toLowerCase() || ''}`)}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.steps'),
            dataIndex: 'steps',
            key: 'steps',
            width: 100,
            render: (steps: TestItemStep[] | undefined, record: TableItem) => {
                if (record.isFolder || record.type !== TestItemType.TEST_CASE) return null;
                return (
                    <Tag color="processing">
                        {t('testItems.steps.count', { count: steps?.length || 0 })}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.checkItems'),
            dataIndex: 'check_items',
            key: 'check_items',
            width: 100,
            render: (checkItems: TestItemCheckItem[] | undefined, record: TableItem) => {
                if (record.isFolder || record.type !== TestItemType.CHECKLIST) return null;
                return (
                    <Tag color="processing">
                        {t('testItems.checkItems.count', { count: checkItems?.length || 0 })}
                    </Tag>
                );
            },
        },
        {
            title: t('testItems.columns.description'),
            dataIndex: 'description',
            key: 'description',
            render: (description: string | null, record: TableItem) => 
                !record.isFolder && description,
        },
        {
            title: t('testItems.columns.components'),
            dataIndex: 'components',
            key: 'components',
            render: (components: string[] | null, record: TableItem) => 
                !record.isFolder && components && components.length > 0 ? (
                    <Space wrap>
                        {components.map((component, index) => (
                            <Tag key={index}>{component}</Tag>
                        ))}
                    </Space>
                ) : null,
        },
        {
            title: t('testItems.columns.folder'),
            dataIndex: 'folder_id',
            key: 'folder',
            render: (folderId: number | null, record: TableItem) => {
                if (record.isFolder) return null;
                const folder = folders.find(f => f.id === folderId);
                return folder ? folder.name : null;
            },
        },
        {
            title: t('testItems.columns.estimate'),
            dataIndex: 'estimate_minutes',
            key: 'estimate',
            render: (minutes: number | null, record: TableItem) => 
                !record.isFolder && minutes ? `${minutes} мин` : null,
        },
        {
            title: t('testItems.columns.labels'),
            dataIndex: 'labels',
            key: 'labels',
            render: (labels: string[] | null, record: TableItem) => 
                !record.isFolder && labels && labels.length > 0 ? (
                    <Space wrap>
                        {labels.map((label, index) => (
                            <Tag key={index}>{label}</Tag>
                        ))}
                    </Space>
                ) : null,
        },
        {
            title: t('testItems.columns.version'),
            dataIndex: 'version',
            key: 'version',
            render: (version: number | null, record: TableItem) => 
                !record.isFolder && version,
        },
        {
            title: t('testItems.columns.createdBy'),
            dataIndex: 'created_by',
            key: 'createdBy',
            render: (createdBy: number | null, record: TableItem) => 
                !record.isFolder && createdBy ? userNames[createdBy] || createdBy.toString() : null,
        },
        {
            title: t('testItems.columns.createdAt'),
            dataIndex: 'created_at',
            key: 'createdAt',
            render: (createdAt: string | null, record: TableItem) => 
                !record.isFolder && createdAt ? new Date(createdAt).toLocaleString() : null,
        },
        {
            title: t('testItems.columns.updatedBy'),
            dataIndex: 'updated_by',
            key: 'updatedBy',
            render: (updatedBy: number | null, record: TableItem) => 
                !record.isFolder && updatedBy ? userNames[updatedBy] || updatedBy.toString() : null,
        },
        {
            title: t('testItems.columns.updatedAt'),
            dataIndex: 'updated_at',
            key: 'updatedAt',
            render: (updatedAt: string | null, record: TableItem) => 
                !record.isFolder && updatedAt ? new Date(updatedAt).toLocaleString() : null,
        },
        {
            title: t('testItems.columns.actions'),
            key: 'actions',
            width: 120,
            render: (_, record: TableItem) => (
                <Space>
                    {!record.isFolder && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEditItem(record as unknown as TestItem)}
                            />
                            <Button
                                type="text"
                                icon={<HistoryOutlined />}
                                onClick={() => navigate(`/projects/${projectId}/test-items/${record.id}/versions`)}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record)}
                            />
                        </>
                    )}
                    {record.isFolder && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => onEditFolder(record as unknown as Folder)}
                            />
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => onDeleteFolder(record as unknown as Folder)}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const visibleColumns = columns.filter(column => {
        const columnKey = column.key as string;
        return columnVisibility[columnKey] !== false;
    });
    console.log('Visible columns details:', visibleColumns.map((col: ColumnType<TableItem>) => ({
        key: col.key,
        title: col.title,
        dataIndex: col.dataIndex
    })));

    const filteredData = getTableData().filter(item => {
        const itemName = item.name || item.title || '';
        const matchesSearch = itemName.toLowerCase().includes(searchText?.toLowerCase() || '');
        const matchesType = !selectedType || item.type === selectedType;
        const matchesPriority = !selectedPriority || item.priority === selectedPriority;
        const matchesSeverity = !selectedSeverity || item.severity === selectedSeverity;
        const matchesStatus = !selectedStatus || item.status === selectedStatus;
        return matchesSearch && matchesType && matchesPriority && matchesSeverity && matchesStatus;
    });

    useEffect(() => {
        if (projectId) {
            loadSharedSteps();
        }
    }, [projectId]);

    const loadSharedSteps = async () => {
        if (projectId) {
            const steps = await sharedStepService.getSharedSteps(projectId, selectedFolderId);
            setSharedSteps(steps);
        }
    };

    const handleAddToFolder = async (step: SharedStep, folderId: number) => {
        try {
            await sharedStepService.updateSharedStep(projectId, step.id, { ...step, folder_id: folderId });
            await loadSharedSteps();
            message.success(t('sharedSteps.addedToFolder'));
        } catch (error) {
            console.error('Error adding step to folder:', error);
            message.error(t('sharedSteps.addToFolderError'));
        }
    };

    const handleCreateSharedStep = async (data: SharedStepCreate) => {
        await sharedStepService.createSharedStep(data);
        await loadSharedSteps();
        message.success(t('sharedSteps.created'));
    };

    const handleUpdateSharedStep = async (step: SharedStep) => {
        await sharedStepService.updateSharedStep(projectId, step.id, step);
        await loadSharedSteps();
        message.success(t('sharedSteps.updated'));
    };

    const handleDeleteSharedStep = async (stepId: number) => {
        await sharedStepService.deleteSharedStep(projectId, stepId);
        await loadSharedSteps();
        message.success(t('sharedSteps.deleted'));
    };

    const handleDataChange = () => {
        // Refresh the test items list
        onEdit({} as TestItem);
    };

    const handleUpdateTestItem = async (data: TestItemCreate): Promise<void> => {
        try {
            if (selectedItem) {
                await testItemService.updateTestItem(selectedItem.id, data);
                message.success(t('testItems.updated'));
            } else {
                await testItemService.createTestItem(data);
                message.success(t('testItems.created'));
            }
            handleCloseForm();
            onDataChange();
        } catch (error) {
            message.error(t('testItems.errors.saveFailed'));
        }
    };

    const handleCloseForm = () => {
        setSelectedItem(null);
    };

    useEffect(() => {
        // Загружаем тестовые данные для всех папок
        const fetchFolderTestData = async () => {
            const testData = await testItemService.getTestData(projectId);
            const folderData: Record<number, TestData[]> = {};
            
            testData.forEach((data: TestData) => {
                if (data.folder_id) {
                    if (!folderData[data.folder_id]) {
                        folderData[data.folder_id] = [];
                    }
                    folderData[data.folder_id].push(data);
                }
            });
            
            setFolderTestData(folderData);
        };
        
        fetchFolderTestData();
    }, [projectId, items]);

    const handleAddTestData = (folder: Folder) => {
        setSelectedFolder(folder);
        setIsTestDataModalVisible(true);
    };

    const fetchFolderTestData = async (folderId: number) => {
        try {
            const data = await testItemService.getTestData(projectId);
            const folderData = data.filter((item: TestData) => item.folder_id === folderId);
            setFolderTestData(prev => ({
                ...prev,
                [folderId]: folderData
            }));
        } catch (error) {
            console.error('Error fetching folder test data:', error);
            message.error('Не удалось загрузить тестовые данные папки');
        }
    };

    const handleTestDataSelect = async (selectedData: TestData[]) => {
        if (!selectedFolder) return;

        try {
            // Обновляем folder_id для выбранных тестовых данных
            await Promise.all(
                selectedData.map(data =>
                    testItemService.updateTestData(projectId, data.id, {
                        ...data,
                        folder_id: selectedFolder.id
                    })
                )
            );

            message.success('Тестовые данные добавлены к папке');
            // Обновляем данные папки
            await fetchFolderTestData(selectedFolder.id);
            if (onDataChange) {
                onDataChange();
            }
            setIsTestDataModalVisible(false);
        } catch (error) {
            message.error('Не удалось добавить тестовые данные к папке');
        }
    };

    return (
        <div className="test-item-list">
            <div style={{ marginBottom: 16 }}>
                <Title level={4}>{t('testItems.title')}</Title>
                <Space style={{ marginBottom: 16 }}>
                    <Search
                        placeholder={t('testItems.search')}
                        onSearch={handleSearch}
                        style={{ width: 200 }}
                    />
                    <Select
                        placeholder={t('testItems.filters.type')}
                        style={{ width: 120 }}
                        onChange={handleTypeChange}
                        allowClear
                    >
                        <Option value={TestItemType.TEST_CASE}>{t('testItems.types.test_case')}</Option>
                        <Option value={TestItemType.CHECKLIST}>{t('testItems.types.checklist')}</Option>
                    </Select>
                    <Select
                        placeholder={t('testItems.filters.priority')}
                        style={{ width: 120 }}
                        onChange={handlePriorityChange}
                        allowClear
                    >
                        <Option value={TestItemPriority.HIGH}>{t('testItems.priorities.high')}</Option>
                        <Option value={TestItemPriority.MEDIUM}>{t('testItems.priorities.medium')}</Option>
                        <Option value={TestItemPriority.LOW}>{t('testItems.priorities.low')}</Option>
                    </Select>
                    <Select
                        placeholder={t('testItems.filters.severity')}
                        style={{ width: 120 }}
                        onChange={handleSeverityChange}
                        allowClear
                    >
                        <Option value={TestItemSeverity.BLOCKER}>{t('testItems.severities.blocker')}</Option>
                        <Option value={TestItemSeverity.MAJOR}>{t('testItems.severities.major')}</Option>
                        <Option value={TestItemSeverity.MINOR}>{t('testItems.severities.minor')}</Option>
                    </Select>
                    <Select
                        placeholder={t('testItems.filters.status')}
                        style={{ width: 120 }}
                        onChange={handleStatusChange}
                        allowClear
                    >
                        <Option value={TestItemStatus.DRAFT}>{t('testItems.statuses.draft')}</Option>
                        <Option value={TestItemStatus.READY}>{t('testItems.statuses.ready')}</Option>
                        <Option value={TestItemStatus.REVIEWED}>{t('testItems.statuses.reviewed')}</Option>
                        <Option value={TestItemStatus.OBSOLETE}>{t('testItems.statuses.obsolete')}</Option>
                        <Option value={TestItemStatus.DEPRECATED}>{t('testItems.statuses.deprecated')}</Option>
                    </Select>
                    <Dropdown overlay={columnVisibilityMenu} trigger={['click']}>
                        <Button icon={<SettingOutlined />}>
                            {t('common.columns')}
                        </Button>
                    </Dropdown>
                </Space>
            </div>

            <Space style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => onEdit({} as TestItem)}
                >
                    {t('testItems.create')}
                </Button>
                <Button
                    type="primary"
                    icon={<ShareAltOutlined />}
                    onClick={() => setSharedStepsDrawerVisible(true)}
                >
                    {t('sharedSteps.title')}
                </Button>
                <Button
                    icon={<FolderOutlined />}
                    onClick={() => onEditFolder({} as Folder)}
                >
                    {t('testItems.createFolder')}
                </Button>
            </Space>

            <Table
                columns={visibleColumns}
                dataSource={getTableData()}
                loading={loading}
                rowKey="key"
                pagination={false}
                scroll={{ x: 'max-content' }}
                expandable={{
                    expandedRowRender: renderExpandedContent,
                    expandedRowKeys: Object.keys(expandedRows).filter(key => expandedRows[key]),
                    onExpand: (expanded, record) => {
                        if (expanded) {
                            setExpandedRows(prev => ({ ...prev, [record.key]: true }));
                        } else {
                            setExpandedRows(prev => ({ ...prev, [record.key]: false }));
                        }
                    }
                }}
            />

            <Drawer
                title={t('sharedSteps.title')}
                placement="right"
                onClose={() => setSharedStepsDrawerVisible(false)}
                open={sharedStepsDrawerVisible}
                width={500}
            >
                <SharedStepsList
                    steps={sharedSteps}
                    projectId={projectId}
                    folderId={selectedFolderId}
                    onAddToFolder={handleAddToFolder}
                    onUpdate={handleUpdateSharedStep}
                    onDelete={handleDeleteSharedStep}
                    onCreate={handleCreateSharedStep}
                    folders={folders}
                />
            </Drawer>

            <AddSharedStepsModal
                open={addStepsModalVisible}
                onClose={() => setAddStepsModalVisible(false)}
                projectId={projectId}
                folderId={selectedFolderForSteps || -1}
                onAddSteps={(step) => {
                    if (selectedFolderForSteps) {
                        handleAddToFolder(step, selectedFolderForSteps);
                    }
                }}
            />

            {selectedItem && (
                <TestItemForm
                    projectId={projectId}
                    folders={folders}
                    initialValues={selectedItem}
                    onSubmit={handleUpdateTestItem}
                    onCancel={handleCloseForm}
                    loading={loading}
                    onDataChange={handleDataChange}
                />
            )}

            <SelectTestDataModal
                visible={isTestDataModalVisible}
                onCancel={() => setIsTestDataModalVisible(false)}
                onSelect={handleTestDataSelect}
                projectId={projectId}
                selectedFolder={selectedFolder}
            />
        </div>
    );
};

export default TestItemList; 